import { verifierCadreCanvas } from '@taki/data-model/cadre-canvas';
import type { ApiCadreSurface, ActionDeSurface, OutilDeSurface, MenuDeSurface, FiltresDeSurface, OptionsDeTitre } from '@taki/ui/cadre-canvas';
import type { CapacitesCadreCanvas } from '@taki/data-model/cadre-canvas';
import { verifierChampsDEcran } from './validation-champs.js';
import type { ComponentType } from 'react';
import type { AppFieldsFile, AppScreen, AppScreensFile, FieldReference } from '@taki/data-model';
import {
  FAMILLES,
  FAMILLE_PERSONNES,
  champsNatifs,
  ecranDeclare,
  estFamille,
  fautesDesEtapes,
  lireChampsDeclares,
  lireEcransDeclares,
  titreDeFiche,
} from '@taki/data-model';
import type { FonctionsDeclarees } from '@taki/ui';
import type { TakiSdk } from '@taki/sdk';
import { HABILLAGES_SOCLE } from '@taki/ui';
import { effetsCites, effetsSocleCites, fenetresCitees, nomsCites, portesCanvas } from './noms-cites.js';

import { effetSocleConnu } from './effets-socle.js';
import { PREFIXE_SURFACE_DE_VUE, clefDeSurfaceDeVue } from './vues-de-fiche-donnees.js';
import type { ProprietesDeVueCanvas } from './vues-de-fiche.js';
import type { EffetDAppDeclaree } from './effets-socle.js';

/**
 * UNE APP EST UNE DECLARATION, ET C'EST TOUT.
 *
 * CE QUE CE MODULE EST. La porte d'entree du regime DECLARE : une app y donne
 * ses trois fichiers (`taki.app.json`, `taki.fields.json`, `taki.screens.json`)
 * et le peu de TypeScript qu'aucun fichier de donnees ne peut porter sans
 * devenir un langage (les fonctions que la declaration NOMME, les effets de ses
 * actions, ses Space Canvas, son jeu de demonstration). En retour elle
 * recoit un objet GELE que l'ecran generique du socle sait monter.
 *
 * CE QU'ELLE N'ECRIT PLUS : l'ecran. Pas de `<Id>Screen.tsx`, pas de `views.ts`,
 * pas de `settings-defs.ts`. Le chargement, les gestes de fiche, l'acces au jeu,
 * les compteurs du menu, les preferences du poste et le panneau de reglages
 * viennent du socle, une fois, pour toutes les apps declarees.
 *
 * TOUT EST VERIFIE A L'IMPORT, ET C'EST LE POINT. Les assembleurs de `@taki/ui`
 * refusent deja un nom introuvable, mais PORTE PAR PORTE, au moment ou la porte
 * s'assemble : une faute sur la troisieme porte ne se voit qu'au clic dessus.
 * Ici, les deux fichiers sont relus par leurs schemas Zod, l'ecran unique est
 * exige, et chaque nom cite est confronte au registre fourni. Un refus arrive
 * donc AVANT le premier pixel, avec le nom fautif, comme partout ailleurs dans
 * le socle.
 *
 * L'APP CANVAS SE DECLARE AUSSI, par la clef `appCanvas` : une fiche qui
 * accorde `canvas.app` et un CHARGEUR de surface, sans aucun des deux fichiers
 * de declaration. Le socle lui fournit le gabarit reduit, le filet d'erreur, le
 * chargement a la demande et son panneau de reglages (`EcranAppCanvas`,
 * `ReglagesAppCanvas`) ; l'app n'ecrit que sa surface. Le guide :
 * `docs/SURFACES-CANVAS.md`.
 *
 * POURQUOI CE PACKAGE ET PAS `@taki/ui`. Le design system ne connait ni Zod, ni
 * famille, ni schema : c'est ecrit en tete de `packages/ui/src/views/fields.ts`
 * et de `views/screens.ts`, et lui donner une dependance vers le modele de
 * donnees echangerait une frontiere nette contre une commodite. L'ecran
 * generique, lui, a besoin des DEUX (les schemas pour lire la declaration, le
 * SDK pour charger) : il vit donc un cran au-dessus.
 */

/* ------------------------------------------------------------------ */
/* Ce qu'une app declaree donne, et ce qu'elle recoit                  */
/* ------------------------------------------------------------------ */

/**
 * UNE FICHE QUI ARRIVE D'UN FICHIER DE DONNEES, donc en `Record` a l'execution.
 *
 * Le type CONCRET (`Contact`, `Asset`) n'existe pas quand la famille est une
 * chaine : l'ecran generique ne connait sa famille qu'au montage
 * (`porte.jeu.family`). C'est exactement ce que `FamilyRepository` rend deja, et
 * ce que les vues du socle consomment.
 */
export type FicheGenerique = Record<string, unknown>;

/**
 * CE QU'UN SPACE CANVAS RECOIT (regle G23). Trois faits, et rien qui suppose un
 * metier : les fiches de sa porte telles que le chargeur les a rendues, la
 * recherche de la zone 2b (un canvas garde l'en-tete entier, donc il doit
 * l'honorer), et l'id de sa porte.
 */
export interface ProprietesDeCanvas extends ApiCadreSurface {
  fiches: readonly FicheGenerique[];
  recherche: string;
  porte: string;
  /**
   * LA FICHE QUE L'ADRESSE OUVRE SUR CETTE PORTE (`?s=<porte>&id=<fiche>`,
   * chantier `fiche-suivi`) : une autre porte y envoie ses fiches
   * (`auClic: { porte }`), un lien recopie la rouvre. `null` : aucune. La
   * surface la montre a sa facon ; le moteur ne la referme pas d'office.
   */
  ficheOuverte: { family: string; id: string } | null;
  /** Referme la fiche ouverte (la surface revient a son etat de porte). */
  fermerFiche(): void;
  /**
   * Ouvre une fiche d'une autre porte de l'app, comme une carte liee : sa puce
   * de sortie (Annuler, Creer, la puce) ramene a cette surface, en un seul
   * Retour.
   */
  ouvrirFiche(porte: string, id: string): void;
  /**
   * Ouvre la fiche vide d'une autre porte de l'app : sa puce, « Annuler » et la
   * fiche creee ramenent a cette surface, en un seul Retour. Rend faux sur un
   * refus (porte inconnue, lecture seule).
   */
  creerFiche(porte: string): boolean;
}

/**
 * LE CONTRAT LIA D'UNE APP CANVAS (`canvas.app.lia`). Sans lui, Lia sait que
 * l'app existe (nom, description, l'ouvrir) mais ne lit RIEN de son contenu.
 * Avec lui, l'app declare ses JEUX de donnees : famille et type (de ses
 * `entities`), champs lisibles, texte brut, contenu riche, et ce que Lia peut
 * y ecrire. Le serveur le relit (`functions/src/ia/canvas-contrat.ts`) et
 * `pnpm apps:check` le valide. Detail : `docs/SURFACES-CANVAS.md`, section 6.
 */
export interface ChampLia {
  /** Un champ natif (`name`, `body`) ou `customFields.<clef>`. */
  cle: string;
  libelle: string;
  /*
   * PAS LE CATALOGUE DES TYPES DE CHAMP (`catalogue-des-types.mjs`), et c'est
   * voulu : ce type dit a Lia comment LIRE et ECRIRE un champ d'une app Canvas,
   * qui n'a pas de `taki.fields.json`. Ni devise, ni formule, ni sequence ne
   * s'y declarent, et le declencheur des fiches ne recalcule ni ne numerote un
   * jeu sans declaration : un `calcul` ou un `numero` y mentirait. Un montant
   * ou une duree s'y dit `number`. Meme liste que `CANVAS_CHAMP_TYPES`
   * (`functions/src/ia/canvas-contrat.ts`, `scripts/check-app-entities.mjs`).
   */
  type: 'text' | 'number' | 'boolean' | 'date' | 'datetime' | 'lien' | 'personne';
  /** Pour un `lien` : l'id du jeu vise. */
  jeu?: string;
  /** Lia peut poser ce champ a la creation. */
  ecriture?: boolean;
}

export interface JeuLia {
  id: string;
  libelle: string;
  description?: string;
  famille: 'contents' | 'groups' | 'messages';
  type: string;
  acces: 'lecture' | 'ecriture';
  titre?: string;
  champs?: readonly ChampLia[];
  /** Les champs dont le texte brut est fait (familles de fiches). */
  texte?: readonly string[];
  /** Les champs sur lesquels Lia peut filtrer (egalite). */
  filtres?: readonly string[];
  tri?: { champ: string; sens: 'asc' | 'desc' };
  /** Le contenu riche d'un jeu `contents` : son format et la version du `FormatDeContenu`. */
  contenu?: { format: 'texte' | 'markdown' | 'prosemirror'; mimeType?: string; formatVersion: number; texteMax?: number };
}

/** Un type tel qu'un import JSON le voit : chaque union de chaines devient `string`. */
type DepuisJson<T> = T extends string ? string
  : T extends readonly (infer U)[] ? readonly DepuisJson<U>[]
    : T extends object ? { [K in keyof T]: DepuisJson<T[K]> }
      : T;

/** Le contrat Lia tel que `taki.app.json` le porte, une fois importe. */
export type ContratLiaDeJson = DepuisJson<ContratLia>;

export interface ContratLia {
  acces: 'aucun' | 'lecture' | 'ecriture';
  jeux?: readonly JeuLia[];
}

/**
 * LA DEROGATION AU GABARIT, telle que `taki.app.json` l'ECRIT (cle `canvas`,
 * regle G23). Le socle n'en lit qu'une chose au montage : la presence de
 * `app`, qui accorde le niveau App Canvas, et ses zones reprises. La forme
 * complete est verifiee par `scripts/check-app-entities.mjs`, sa verite par
 * `scripts/check-canvas.mjs`.
 */
export interface CanvasDeFiche {
  /** Le jour ou le fondateur a accorde la derogation (AAAA-MM-JJ). */
  grantedOn: string;
  /**
   * Le niveau App Canvas : `keeps` dit ce que l'en-tete REDUIT reprend (2b, 2c)
   * et, depuis le 05/10/2026, ce que la rangee 3 GARDE (3a les actions, 3b le
   * menu « ... »), que la surface nourrit par `poserActions` et `poserOutils` ;
   * `lia` ce que la surface accepte que Lia lise de son contenu.
   */
  /*
   * `lia` arrive d'un `import appInfo from './taki.app.json'` : TypeScript
   * elargit alors chaque chaine du JSON en `string`, et `'lecture'` ne serait
   * plus un `ContratLia`. La forme exacte est verifiee par
   * `scripts/check-app-entities.mjs` et `pnpm canvas:check`, pas ici.
   */
  app?: { keeps?: readonly string[]; elements?: readonly string[]; alimente?: readonly string[]; lia?: ContratLiaDeJson };
  /** Les Space Canvas, une entree par porte custom. */
  spaces?: ReadonlyArray<{ id: string; keeps?: readonly string[]; elements?: readonly string[]; alimente?: readonly string[] }>;
  /** Les View Canvas, par id de vue namespace. */
  views?: readonly string[];
}

/** La fiche d'identite d'une app, telle que `taki.app.json` la porte. */
export interface FicheApp {
  id: string;
  name: string;
  description?: string;
  /** Nom d'une icone `@taki/ui` (`IconContactBook`), resolu par le registre d'icones. */
  icon: string;
  menu: { label: string; route: string };
  /** Les donnees que l'app touche : le shell en derive ses PORTES d'acces. */
  entities?: ReadonlyArray<{ name: string; types?: readonly string[]; scope?: string }>;
  /** Les creations proposees comme raccourcis du dock. */
  actions?: ReadonlyArray<{ id: string; label: string; icon: string }>;
  /** La derogation au gabarit, quand le fondateur en a accorde une (regle G23). */
  canvas?: CanvasDeFiche;
}

/**
 * CE QU'UNE SURFACE D'APP CANVAS RECOIT, et rien qui suppose un metier.
 *
 * `recherche` est la valeur de la zone 2b quand la fiche la reprend (`keeps`
 * contient `2b`), la chaine vide sinon : une surface qui l'ignore alors qu'elle
 * est reprise laisse un champ mort (regle G23). `poserStats` nourrit la zone
 * 2c, meme regle ; appele sans que la fiche reprenne 2c, il ne rend rien.
 *
 * LA RANGEE 3 SE NOURRIT DE LA MEME FACON (depuis le 05/10/2026, retour du
 * fondateur : la rangee native reste visible autour d'une App Canvas).
 * `poserActions` pose les actions de 3a, rendues PAR le gabarit avec ses
 * regles (« + Ajouter » en lime et en premier, G5 ; un seul lime, G14 ; trois
 * mots, G13 ; une raison sur tout bouton eteint, G16) ; `poserOutils` pose les
 * entrees du menu « ... » tout a droite (3b), les gestes rares. Les deux
 * exigent que la fiche GARDE la zone (`keeps`) : poser hors d'une zone gardee
 * est REFUSE, avec le nom (`rangee-de-surface.ts`). Les actions appartiennent
 * a ce que la surface montre (G7) : une galerie et un contenu ouvert n'en
 * posent pas les memes, et la surface les repose quand elle change d'etat.
 * Identites STABLES, comme `poserStats` : une surface peut les mettre dans les
 * dependances d'un effet.
 */
export interface ProprietesDeSurface extends ApiCadreSurface {
  appId: string;
  poserFiche?(fiche: { family: string; recordId: string; titre: string } | null): void;
}
export type { OptionsDeTitre } from '@taki/ui/cadre-canvas';

/**
 * LA SURFACE D'UNE APP CANVAS, donnee par un CHARGEUR et jamais par un
 * composant.
 *
 * C'EST CE QUI GARANTIT LE CHARGEMENT A LA DEMANDE PAR CONSTRUCTION : l'app
 * ecrit `charger: () => import('./Surface.js')`, et la surface avec son moteur
 * (souvent plusieurs centaines de Ko) partent dans un morceau a part, que le
 * navigateur ne va chercher que si l'on ouvre l'app. Un composant passe en
 * clair entrerait dans le bundle de CHAQUE client, meme la ou l'app n'est pas
 * installee, sans que personne y ait pense.
 */
export interface SurfaceDAppCanvas {
  capacites?: CapacitesCadreCanvas;
  charger: () => Promise<{ default: ComponentType<ProprietesDeSurface> }>;
}

/**
 * UNE SURFACE DU CATALOGUE `canvas` : celle d'une porte (`ProprietesDeCanvas`)
 * ou celle d'une vue (`ProprietesDeVueCanvas`, clef `vue:<id>`).
 */
export type SurfaceCanvas = ComponentType<ProprietesDeCanvas> | ComponentType<ProprietesDeVueCanvas>;

/**
 * CE QU'UNE FENETRE D'APP RECOIT (clef `fenetres` de `definirAppDeclaree`) : la
 * fiche ouverte, BRUTE comme `ProprietesDeVueCanvas.fiche`, la porte qui la
 * montre, et de quoi se fermer. La fenetre est un composant React ordinaire de
 * l'app : elle utilise `useAppRuntime`, `useNavigationPort`, `usePersonnesPort`
 * librement et rend son propre `Modal`. L'ecran la monte a cote du gabarit au
 * clic d'un geste `niveau: "fiche"` + `fenetre`, et la demonte a `fermer()`, au
 * changement de fiche ou de porte (`docs/socle/actions-fiche-sans-plafond.md`).
 */
export interface ProprietesDeFenetreDeFiche {
  fiche: FicheGenerique;
  /** L'id de la porte qui montre la fiche. */
  porte: string;
  fermer(): void;
}

/** Ce qu'une app passe a `definirAppDeclaree`. */
export interface DefinitionApp {
  /** `import fiche from './taki.app.json'`. */
  fiche: FicheApp;
  /**
   * `import champs from './taki.fields.json'`, non verifie par TypeScript.
   * OBLIGATOIRE sauf pour une App Canvas, qui n'en a pas (voir `appCanvas`).
   */
  champs?: unknown;
  /**
   * `import ecrans from './taki.screens.json'`, non verifie par TypeScript.
   * OBLIGATOIRE sauf pour une App Canvas, qui n'en a pas (voir `appCanvas`).
   */
  ecrans?: unknown;
  /**
   * L'APP CANVAS (regle G23) : l'app entiere est UNE surface, sans rail, sans
   * porte, sans vue du socle. Le niveau se DECLARE dans la fiche
   * (`canvas.app`), et cette clef fournit la surface qui y repond. Exclusive :
   * une App Canvas ne donne ni `champs`, ni `ecrans`, ni `fonctions`, ni
   * `effets`, ni `canvas`, parce qu'elle n'a aucune porte pour les porter.
   */
  appCanvas?: SurfaceDAppCanvas;
  /**
   * LE REGISTRE DE FONCTIONS : tout ce que la declaration NOMME sans le decrire
   * (une valeur deduite, un habillage, un catalogue, un predicat, un format).
   * Absent quand la declaration ne cite aucun nom.
   */
  fonctions?: FonctionsDeclarees<FicheGenerique>;
  /**
   * LES EFFETS DES ACTIONS, par leur nom. Une action d'AJOUT n'en a pas : la
   * creation est une fiche vide tenue par le socle (regle G6). Les autres
   * actions en nomment un, et un nom que ce registre ne porte pas est refuse
   * ici, avec son nom : un bouton muet serait un bouton qui ment.
   *
   * L'EFFET RECOIT LE CONTEXTE DU CLIC (la porte, la fiche ouverte, la
   * selection, le SDK et l'org) ; un `() => void` ecrit avant reste valide.
   * Les effets `socle:<nom>` ne s'ecrivent PAS ici : le runtime les fournit.
   */
  effets?: Readonly<Record<string, EffetDAppDeclaree>>;
  /**
   * LES FENETRES D'APP, par leur nom : ce que `fenetre` d'une action
   * `niveau: "fiche"` nomme. Meme regle que `effets`, dans les deux sens : un
   * nom que la declaration cite sans que l'app le fournisse est refuse, et une
   * fenetre fournie qu'aucune action ne nomme aussi.
   */
  fenetres?: Readonly<Record<string, ComponentType<ProprietesDeFenetreDeFiche>>>;
  /**
   * LES SURFACES CANVAS de l'app (regle G23) : par id de PORTE, la surface d'une
   * porte Canvas ; par `vue:<id de vue>`, le rendu d'une vue Canvas (niveau
   * collection ou fiche) que `vuesCustom` decrit. La derogation se demande
   * AVANT au fondateur et se DECLARE dans `taki.app.json` (`canvas.spaces`,
   * `canvas.views`) ; ce registre ne fait que fournir ce qu'elle reclame.
   */
  canvas?: Readonly<Record<string, SurfaceCanvas>>;
  /**
   * LE JEU DE DEMONSTRATION. L'app ne fournit que l'import ; le socle pose la
   * ligne dans Parametres (reglages generaux de l'app), avec les briques du
   * gabarit. Une app n'ecrit aucun ecran de reglages.
   */
  demo?: DemoDAppDeclaree;
}

/** Ce que l'import d'un jeu de demonstration recoit. */
export interface ContexteDeDemo {
  sdk: TakiSdk;
  orgId: string;
  /** La personne connectee dans l'annuaire, `null` si inconnue. */
  moi: string | null;
}

/** Le jeu de demonstration d'une app, rendu par le socle dans Parametres. */
export interface DemoDAppDeclaree {
  /** Les jeux ecrits : sans droit d'ecriture sur l'un d'eux, le bouton se grise. */
  ecrit?: readonly { family: string; type?: string }[];
  /** Reserve aux emulateurs locaux : la ligne n'existe pas ailleurs. */
  local?: boolean;
  /** Importe ce qui manque ; rend le compte rendu affiche. */
  importer(contexte: ContexteDeDemo): Promise<{ message: string }>;
}

/** Une app declaree, prete a monter : sa declaration validee et ses registres. */
export interface AppDeclaree {
  readonly id: string;
  readonly fiche: FicheApp;
  readonly champs: AppFieldsFile;
  /** Le fichier d'ecrans entier, tel qu'il a ete valide. */
  readonly ecrans: AppScreensFile;
  /** L'ecran UNIQUE de l'app (v1 : une app = un ecran, ses portes portent tout). */
  readonly ecran: AppScreen;
  readonly fonctions: FonctionsDeclarees<FicheGenerique>;
  readonly effets: Readonly<Record<string, EffetDAppDeclaree>>;
  readonly fenetres: Readonly<Record<string, ComponentType<ProprietesDeFenetreDeFiche>>>;
  readonly canvas: Readonly<Record<string, SurfaceCanvas>>;
  readonly demo: DemoDAppDeclaree | undefined;
  /**
   * La surface d'une App Canvas, `null` pour une app a portes. Quand elle est
   * la, `champs`, `ecrans` et `ecran` sont les constantes VIDES ci-dessous :
   * rien ne les lit, et les garder types evite a chaque lecteur un cas `null`.
   */
  readonly appCanvas: SurfaceDAppCanvas | null;
}

/* ------------------------------------------------------------------ */
/* Les declarations vides d'une App Canvas                             */
/* ------------------------------------------------------------------ */

/*
 * TROIS CONSTANTES QUI NE PASSENT PAS PAR ZOD, et c'est voulu : les schemas
 * exigent au moins un jeu, un ecran, une porte (`portes.min(1)`), parce qu'une
 * app a portes sans porte est une faute. Une App Canvas n'en a aucune, par
 * nature : ces litteraux sont types a la main, et GELES en profondeur, pour
 * qu'aucun lecteur ne puisse les remplir par megarde et faire apparaitre une
 * porte que personne n'a declaree.
 */

/** L'ecran vide d'une App Canvas : aucun lecteur ne doit y trouver de porte. */
export const ECRAN_VIDE: AppScreen = Object.freeze({
  id: 'app-canvas',
  vues: Object.freeze([]) as unknown as AppScreen['vues'],
  arrivee: 'core.liste',
  vb: Object.freeze({}) as AppScreen['vb'],
  portes: Object.freeze([]) as unknown as AppScreen['portes'],
}) as AppScreen;

/** Le fichier d'ecrans vide d'une App Canvas. */
export const ECRANS_VIDES: AppScreensFile = Object.freeze({
  version: 1,
  ecrans: Object.freeze([ECRAN_VIDE]) as unknown as AppScreensFile['ecrans'],
}) as AppScreensFile;

/** Le fichier de champs vide d'une App Canvas. */
export const CHAMPS_VIDES: AppFieldsFile = Object.freeze({
  version: 1,
  jeux: Object.freeze([]) as unknown as AppFieldsFile['jeux'],
}) as AppFieldsFile;

/* ------------------------------------------------------------------ */
/* Les refus                                                           */
/* ------------------------------------------------------------------ */

/** Le refus commun : l'app fautive d'abord, la raison ensuite. */
function refus(id: string, raison: string): never {
  throw new Error(`App declaree "${id}" : ${raison}`);
}

/**
 * CHAQUE NOM CITE EXISTE, OU L'APP NE MONTE PAS.
 *
 * LE REGISTRE DU SOCLE EST LE REPLI DES HABILLAGES ET DES FORMATS, et dans cet
 * ordre : une app qui nomme `euros` a elle sert le sien, une app nee du Studio
 * n'en ecrit aucun et recoit celui du socle (`roleDe`, `statsDe`). Refuser un
 * nom que le socle porte ferait refuser des apps qui marchent.
 */
function verifierLesNoms(
  id: string,
  ecran: AppScreen,
  fonctions: FonctionsDeclarees<FicheGenerique>,
): void {
  for (const cite of nomsCites(ecran)) {
    const sien = fonctions[cite.espace]?.[cite.nom];
    if (sien !== undefined) continue;
    const socle =
      cite.espace === 'habillages' || cite.espace === 'formats'
        ? HABILLAGES_SOCLE[cite.nom]
        : undefined;
    if (socle !== undefined) continue;
    refus(
      id,
      `${cite.ou} cite "${cite.nom}", absent du registre FONCTIONS.${cite.espace} de l'app.`,
    );
  }
}

/**
 * Chaque effet nomme par une action est fourni, et chaque effet fourni sert.
 * Un effet `socle:` n'est pas a fournir, mais il doit EXISTER : le runtime n'a
 * rien a brancher sur un nom qu'il ne connait pas.
 */
function verifierLesEffets(
  id: string,
  ecran: AppScreen,
  effets: Readonly<Record<string, EffetDAppDeclaree>>,
): void {
  for (const cite of effetsSocleCites(ecran)) {
    if (!effetSocleConnu(cite.nom)) {
      refus(id, `${cite.ou} nomme l'effet "${cite.nom}", que le socle ne fournit pas.`);
    }
  }
  const cites = effetsCites(ecran);
  for (const cite of cites) {
    if (effets[cite.nom] === undefined) {
      refus(id, `${cite.ou} nomme l'effet "${cite.nom}", que l'app ne fournit pas.`);
    }
  }
  const nommes = new Set(cites.map((cite) => cite.nom));
  for (const nom of Object.keys(effets)) {
    if (!nommes.has(nom)) {
      refus(id, `l'effet "${nom}" est fourni, aucune action de la declaration ne le nomme.`);
    }
  }
}

/**
 * Chaque fenetre nommee par un geste de fiche est fournie, et chaque fenetre
 * fournie sert : les deux sens, comme les effets.
 */
function verifierLesFenetres(
  id: string,
  ecran: AppScreen,
  fenetres: Readonly<Record<string, ComponentType<ProprietesDeFenetreDeFiche>>>,
): void {
  const citees = fenetresCitees(ecran);
  for (const cite of citees) {
    if (fenetres[cite.nom] === undefined) {
      refus(id, `${cite.ou} nomme la fenetre "${cite.nom}", que l'app ne fournit pas.`);
    }
  }
  const nommees = new Set(citees.map((cite) => cite.nom));
  for (const nom of Object.keys(fenetres)) {
    if (!nommees.has(nom)) {
      refus(id, `la fenetre "${nom}" est fournie, aucune action de la declaration ne la nomme.`);
    }
  }
}

/**
 * CHAQUE PORTE CANVAS A SA SURFACE, ET CHAQUE SURFACE A SA PORTE.
 *
 * LES DEUX SENS, parce qu'une derogation qui traine est une derogation qu'on
 * oublie d'avoir accordee : c'est deja la regle de `scripts/check-canvas.mjs`,
 * tenue ici au montage sur la seule chose que la CI ne peut pas lire, le
 * registre de composants.
 */
function verifierLesCanvas(
  id: string,
  ecran: AppScreen,
  canvas: Readonly<Record<string, SurfaceCanvas>>,
  vuesAccordees: readonly string[],
): void {
  const declarees = portesCanvas(ecran);
  for (const porte of declarees) {
    if (canvas[porte] === undefined) {
      refus(id, `la porte "${porte}" declare un canvas, l'app ne fournit aucune surface pour elle.`);
    }
  }
  /*
   * LES VUES CANVAS (niveaux Vue et Fiche, chantier `fiche-suivi`), MEMES DEUX
   * SENS : une carte de `vuesCustom` sans composant `vue:<id>` serait une
   * entree de selecteur qui ne rend rien, un composant sans carte une
   * derogation oubliee. Et l'ACCORD (G23) : la vue est nommee dans
   * `canvas.views` de la fiche, comme `pnpm canvas:check` le tient en CI.
   */
  const vues = (ecran.vuesCustom ?? []).map((vue) => vue.id);
  for (const vue of vues) {
    if (canvas[clefDeSurfaceDeVue(vue)] === undefined) {
      refus(id, `la vue custom "${vue}" est decrite, l'app ne fournit aucun composant canvas['${clefDeSurfaceDeVue(vue)}'].`);
    }
    if (!vuesAccordees.includes(vue)) {
      refus(id, `la vue custom "${vue}" n'est pas accordee : canvas.views de taki.app.json ne la nomme pas (regle G23).`);
    }
  }
  for (const clef of Object.keys(canvas)) {
    if (clef.startsWith(PREFIXE_SURFACE_DE_VUE)) {
      if (!vues.includes(clef.slice(PREFIXE_SURFACE_DE_VUE.length))) {
        refus(id, `un composant canvas est fourni pour "${clef}", qu'aucune vuesCustom ne decrit.`);
      }
      continue;
    }
    if (!declarees.includes(clef)) {
      refus(id, `une surface canvas est fournie pour "${clef}", qui ne declare aucun canvas.`);
    }
  }
}

/**
 * LE CHAMP DES ETAPES D'UNE PORTE EST UNE LISTE DE SON JEU (chantier
 * `fiche-suivi`). Un champ NATIF se verifie dans le schema (son catalogue vit
 * dans les colonnes de l'ecran) ; un champ DECLARE, contre `taki.fields.json` :
 * une liste a choix unique, parce qu'un clic pose UNE valeur. La regle vit
 * dans `fautesDesEtapes` (`@taki/data-model`), que le moteur du Studio et son
 * serveur lisent aussi : une seule implementation, et une instance fautive ne
 * passe plus l'enregistrement pour disparaitre au montage.
 */
function verifierLesEtapes(id: string, champs: AppFieldsFile, ecran: AppScreen): void {
  const [faute] = fautesDesEtapes({ champs, ecrans: { version: 1, ecrans: [ecran] } });
  if (faute !== undefined) refus(id, faute);
}

/**
 * CE QU'UNE REFERENCE A L'ANNUAIRE DES PERSONNES PEUT LIRE EN TITRE : le nom ou
 * l'adresse, rien d'autre. `personnes` n'est pas une famille du SDK (comme
 * `members`), donc `champsNatifs` ne la connait pas ; et l'annuaire ne porte
 * rien d'autre qui se montre (son `userId` est un identifiant).
 */
const NATIFS_DE_PERSONNE: readonly string[] = ['displayName', 'email'];

/**
 * CHAQUE REFERENCE VISE UNE FAMILLE QUI EXISTE, et un titre qu'elle porte.
 *
 * Une colonne `lien` designe une famille SANS qu'une porte la monte : rien
 * d'autre que cette verification ne recoupera donc jamais son nom avec le
 * socle. Une famille inconnue chargerait un annuaire sur une collection qui
 * n'existe pas, sans erreur, et la colonne montrerait des ids pour toujours.
 * Un `titre` natif (`socle:legalName`) absent du schema de la famille rendrait
 * `undefined` sur chaque fiche : la precedence retomberait sur la convention,
 * et la declaration mentirait en silence. Un titre du sac (`enseigne`), lui,
 * ne se verifie pas ici : le jeu vise appartient peut-etre a une autre app.
 *
 * Le fichier de champs comme le catalogue de l'ecran : les deux endroits ou une
 * reference se declare (`docs/VUES-CONTRAT.md`, 16.10).
 */
function verifierLesReferences(id: string, champs: AppFieldsFile, ecran: AppScreen): void {
  const verifier = (ou: string, reference: FieldReference): void => {
    const personnes = reference.family === FAMILLE_PERSONNES;
    if (!personnes && !estFamille(reference.family)) {
      refus(id, `${ou} vise la famille "${reference.family}", que le socle ne connait pas.`);
    }
    for (const nom of [reference.titre, reference.sousTitre]) {
      if (nom === undefined || !nom.startsWith('socle:')) continue;
      const natif = nom.slice('socle:'.length);
      const connus = personnes
        ? NATIFS_DE_PERSONNE
        : champsNatifs(reference.family).map((champ) => champ.key);
      if (!connus.includes(natif)) {
        refus(
          id,
          `${ou} lit "${nom}" sur la famille "${reference.family}", qui n'a pas ce champ natif.`,
        );
      }
    }
  };
  for (const jeu of champs.jeux) {
    for (const champ of jeu.champs) {
      if (champ.reference === undefined) continue;
      verifier(`le champ "${champ.key}" du jeu ${jeu.family}/${jeu.type ?? '*'}`, champ.reference);
    }
  }
  for (const colonne of ecran.colonnes ?? []) {
    if (colonne.reference === undefined) continue;
    verifier(`la colonne "${colonne.champ}" de l'ecran "${ecran.id}"`, colonne.reference);
  }
}

/**
 * LES TITRES DU SOCLE, UNE FONCTION PAR FAMILLE, SOUS CEUX DE L'APP.
 *
 * `@taki/ui` ne connait aucune famille : c'est ici que la convention du
 * data-model (`titreDeFiche`) entre dans l'ecran, pour que toute colonne `lien`
 * ait un mot a montrer sans rien declarer. L'app passe DEVANT : un registre qui
 * nomme une famille la raconte mieux qu'une regle generale.
 */
function fonctionsAvecTitresDuSocle(
  fonctions: FonctionsDeclarees<FicheGenerique>,
): FonctionsDeclarees<FicheGenerique> {
  const titres: Record<string, (fiche: Record<string, unknown>) => string | undefined> = {};
  for (const famille of FAMILLES) titres[famille] = (fiche) => titreDeFiche(famille, fiche);
  /* L'ANNUAIRE DES PERSONNES a sa convention aussi (le nom, sinon l'adresse),
     bien qu'il ne soit pas une famille du SDK : c'est elle qui fait dire un nom
     a une colonne `personne` sans rien declarer. */
  titres[FAMILLE_PERSONNES] = (fiche) => titreDeFiche(FAMILLE_PERSONNES, fiche);
  return { ...fonctions, titres: { ...titres, ...fonctions.titres } };
}

/* ------------------------------------------------------------------ */
/* Definir                                                             */
/* ------------------------------------------------------------------ */

/**
 * DEFINIR UNE APP DECLAREE : lire ses fichiers, les verifier, geler le tout.
 *
 * UN SEUL ECRAN, ET C'EST LA V1. Une app est UN ecran dont les PORTES portent
 * les jeux : c'est deja ce que font Contact (deux portes) et Timesheet (trois).
 * Un second ecran demanderait une route de plus dans le shell, un menu qui
 * distingue ecran et porte, et une preference par ecran : rien de tout ca n'est
 * decide. On refuse donc, plutot que de monter le premier et de taire l'autre.
 */
export function definirAppDeclaree(definition: DefinitionApp): AppDeclaree {
  const id = definition.fiche.id;
  if (definition.appCanvas !== undefined) return definirAppCanvas(definition);

  /* UNE APP A PORTES SANS SES DEUX FICHIERS NE MONTE RIEN. Le dire par son nom,
     plutot que de laisser Zod repondre « Required » sur `undefined`. */
  if (definition.champs === undefined || definition.ecrans === undefined) {
    refus(
      id,
      'taki.fields.json et taki.screens.json sont obligatoires : une app a portes les donne tous ' +
        "les deux. Seule une App Canvas (clef appCanvas) s'en passe.",
    );
  }
  /* LE NIVEAU APP ACCORDE SANS SURFACE : l'ecran generique ne l'appliquerait
     jamais, et la fiche accorderait une derogation a laquelle rien ne repond.
     C'est la derogation morte que `pnpm canvas:check` refuse, tenue ici au
     montage. */
  if (definition.fiche.canvas?.app !== undefined) {
    refus(
      id,
      'la fiche accorde canvas.app, et definirAppDeclaree ne recoit aucune clef appCanvas : ' +
        "le niveau App Canvas n'a pas de surface.",
    );
  }
  const champs = lireChampsDeclares(definition.champs, `${id}/taki.fields.json`);
  const ecrans = lireEcransDeclares(definition.ecrans, `${id}/taki.screens.json`);

  if (ecrans.ecrans.length !== 1) {
    const connus = ecrans.ecrans.map((e) => e.id).join(', ');
    refus(
      id,
      `taki.screens.json declare ${String(ecrans.ecrans.length)} ecrans (${connus || 'aucun'}) : ` +
        "une app declaree en porte UN, et ce sont ses PORTES qui portent ses jeux.",
    );
  }
  /* L'ecran est celui que le fichier declare, lu par son id : `ecranDeclare`
     refuse deja un id absent, et il n'y en a qu'un ici. */
  const ecran = ecranDeclare(ecrans, ecrans.ecrans[0]?.id ?? '');

  /*
   * UNE VUE CUSTOM D'UNE APP DECLAREE EST UN CANVAS (chantier `fiche-suivi`) :
   * sa carte dans `vuesCustom`, son composant dans `canvas['vue:<id>']`, son
   * accord dans `canvas.views` de la fiche. `verifierLesCanvas` tient les
   * trois. Avant ce chantier, le regime declare refusait toute vue custom.
   */

  const fonctions = fonctionsAvecTitresDuSocle(definition.fonctions ?? {});
  const effets = definition.effets ?? {};
  const fenetres = definition.fenetres ?? {};
  const canvas = definition.canvas ?? {};

  verifierChampsDEcran(champs, ecran);
  verifierLesNoms(id, ecran, fonctions);
  verifierLesEffets(id, ecran, effets);
  verifierLesFenetres(id, ecran, fenetres);
  verifierLesCanvas(id, ecran, canvas, definition.fiche.canvas?.views ?? []);
  verifierLesEtapes(id, champs, ecran);
  for (const porte of ecran.portes) if (porte.canvas?.elements !== undefined) verifierCadreCanvas({ level: 'space', ...porte.canvas }, { moteur: true, portes: true });
  verifierLesReferences(id, champs, ecran);

  return Object.freeze({
    id,
    fiche: definition.fiche,
    champs,
    ecrans,
    ecran,
    fonctions,
    effets,
    fenetres,
    canvas,
    demo: demoValide(id, definition.demo),
    appCanvas: null,
  });
}

/** Le jeu de demonstration, s'il est fourni, doit savoir s'importer. */
function demoValide(id: string, demo: DemoDAppDeclaree | undefined): DemoDAppDeclaree | undefined {
  if (demo === undefined) return undefined;
  if (typeof demo.importer !== 'function') refus(id, 'demo.importer doit etre une fonction : (contexte) => Promise<{ message }>.');
  return Object.freeze({ ...demo });
}

/**
 * DEFINIR UNE APP CANVAS : la fiche, la surface, et rien d'autre.
 *
 * CE QUI EST REFUSE, ET POURQUOI C'EST UN REFUS ET PAS UN OUBLI TOLERE. Une App
 * Canvas n'a pas de porte (le niveau retire le rail, `docs/GABARIT-ECRAN.md`,
 * regle G23) : des champs, des ecrans, des fonctions citees, des effets
 * d'actions ou des surfaces de Space Canvas n'auraient AUCUN lecteur. Les
 * accepter en silence ferait croire a l'auteur que sa declaration sert.
 *
 * LA DEROGATION SE DECLARE DANS LA FICHE (regle G23) : sans `canvas.app`, la
 * surface serait un ecran hors gabarit que personne n'a accorde. C'est le refus
 * que `pnpm canvas:check` tient en CI ; ici il arrive avant le premier pixel.
 */
function definirAppCanvas(definition: DefinitionApp): AppDeclaree {
  const id = definition.fiche.id;
  const surface = definition.appCanvas;
  if (surface === undefined || typeof surface.charger !== 'function') {
    refus(id, "appCanvas.charger doit etre une fonction : () => import('./Surface.js').");
  }
  if (definition.fiche.canvas?.app?.elements !== undefined) verifierCadreCanvas({ level: 'app', ...definition.fiche.canvas.app }, surface.capacites);
  const enTrop = (['champs', 'ecrans', 'fonctions', 'effets', 'fenetres', 'canvas'] as const).filter(
    (clef) => definition[clef] !== undefined,
  );
  if (enTrop.length > 0) {
    refus(
      id,
      `une App Canvas n'a pas de porte : rien a declarer (${enTrop.join(', ')} fourni). ` +
        'Retirer ces clefs, ou revenir a une app a portes sans appCanvas.',
    );
  }
  if (definition.fiche.canvas?.app === undefined) {
    refus(
      id,
      'appCanvas est fourni, la fiche ne declare pas canvas.app : le niveau app se DECLARE dans ' +
        'taki.app.json, apres accord du fondateur (regle G23).',
    );
  }
  return Object.freeze({
    id,
    fiche: definition.fiche,
    champs: CHAMPS_VIDES,
    ecrans: ECRANS_VIDES,
    ecran: ECRAN_VIDE,
    fonctions: Object.freeze({}) as FonctionsDeclarees<FicheGenerique>,
    effets: Object.freeze({}),
    fenetres: Object.freeze({}),
    canvas: Object.freeze({}),
    demo: demoValide(id, definition.demo),
    appCanvas: Object.freeze({ ...surface }),
  });
}
